import { useI18n, type MessageKey } from '@ecom/i18n';
import { Badge, type BadgeTone } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import type { AttentionReason, ShipmentStatus } from '../types';

/** Every status a shipment can hold — `Record` so a missing one fails to compile. */
const TONE: Record<ShipmentStatus, BadgeTone> = {
  pending_dispatch: 'warn',
  dispatch_failed: 'critical',
  registered: 'info',
  pickup_scheduled: 'info',
  in_transit: 'info',
  out_for_delivery: 'accent',
  delivered: 'good',
  return_in_progress: 'warn',
  returned: 'neutral',
  under_review: 'warn',
  cancelled: 'neutral',
  lost: 'critical',
  unknown: 'neutral',
};

export const SHIPMENT_STATUS_KEY: Record<ShipmentStatus, MessageKey> = {
  pending_dispatch: 'merchant.ship.status.pending_dispatch',
  dispatch_failed: 'merchant.ship.status.dispatch_failed',
  registered: 'merchant.ship.status.registered',
  pickup_scheduled: 'merchant.ship.status.pickup_scheduled',
  in_transit: 'merchant.ship.status.in_transit',
  out_for_delivery: 'merchant.ship.status.out_for_delivery',
  delivered: 'merchant.ship.status.delivered',
  return_in_progress: 'merchant.ship.status.return_in_progress',
  returned: 'merchant.ship.status.returned',
  under_review: 'merchant.ship.status.under_review',
  cancelled: 'merchant.ship.status.cancelled',
  lost: 'merchant.ship.status.lost',
  unknown: 'merchant.ship.status.unknown',
};

export const ATTENTION_KEY: Record<AttentionReason, MessageKey> = {
  under_review: 'merchant.ship.attention.under_review',
  cancelled: 'merchant.ship.attention.cancelled',
  lost: 'merchant.ship.attention.lost',
  unknown_status: 'merchant.ship.attention.unknown_status',
  unexpected_transition: 'merchant.ship.attention.unexpected_transition',
  credentials: 'merchant.ship.attention.credentials',
  return: 'merchant.ship.attention.return',
  cancellation_refused: 'merchant.ship.attention.cancellation_refused',
};

/**
 * What a failed send's CODE means to the merchant. A code not listed here is shown as itself, under « Code » — never
 * replaced by a guess.
 */
const LAST_ERROR_KEY: Readonly<Record<string, MessageKey>> = {
  INVALID_PHONE: 'merchant.ship.lastError.INVALID_PHONE',
  INVALID_CITY: 'merchant.ship.lastError.INVALID_CITY',
  INVALID_ADDRESS: 'merchant.ship.lastError.INVALID_ADDRESS',
  INVALID_NAME: 'merchant.ship.lastError.INVALID_NAME',
  INVALID_REQUEST: 'merchant.ship.lastError.INVALID_REQUEST',
  INVALID_CLIENT: 'merchant.ship.lastError.credentials',
  CLIENT_INACTIVE: 'merchant.ship.lastError.credentials',
  CARRIER_ACCOUNT_UNAVAILABLE: 'merchant.ship.lastError.credentials',
  FORBIDDEN: 'merchant.ship.lastError.FORBIDDEN',
  ECOMMERCE_STORE_NOT_FOUND: 'merchant.ship.lastError.ECOMMERCE_STORE_NOT_FOUND',
  CARRIER_NOT_CONFIGURED: 'merchant.ship.lastError.CARRIER_NOT_CONFIGURED',
};

export function lastErrorKey(code: string): MessageKey | undefined {
  return LAST_ERROR_KEY[code];
}

export function ShipmentStatusBadge({ status }: { status: ShipmentStatus }): ReactElement {
  const { t } = useI18n();
  return <Badge tone={TONE[status]}>{t(SHIPMENT_STATUS_KEY[status])}</Badge>;
}

export function AttentionBadge({ reason }: { reason: AttentionReason }): ReactElement {
  const { t } = useI18n();
  return <Badge tone="critical">{t(ATTENTION_KEY[reason])}</Badge>;
}
